// frontend/src/pages/PlannedAdherence.js
// Analytics — planned adherence dashboard: output due (by due date) vs
// orders received (by order date). All values ex VAT; cancelled orders excluded.
// KPI cards and chart bars drill into the Data Table; table rows open the order.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { manufacturingAPI } from '../services/api';

// Validated categorical palette (dataviz reference, light mode):
// slot 1 = output due, slot 2 = orders received. Fixed assignment, never cycled.
const COLOR_DUE = '#2a78d6';
const COLOR_RECEIVED = '#eb6834';
const INK_MUTED = '#898781';
const GRIDLINE = '#e1e0d9';
const OVERDUE_RED = '#d03b3b';

const gbp0 = new Intl.NumberFormat('en-GB', {
  style: 'currency', currency: 'GBP', maximumFractionDigits: 0,
});
const gbp2 = new Intl.NumberFormat('en-GB', {
  style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2,
});

const STATUS_LABELS = {
  new: 'New',
  confirmed: 'Confirmed',
  in_production: 'In Production',
  ready_to_ship: 'Ready to Ship',
  partially_shipped: 'Partially Shipped',
  complete: 'Complete',
};
const ALL_STATUS_KEYS = Object.keys(STATUS_LABELS);
const OPEN_STATUSES = new Set(['new', 'confirmed', 'in_production', 'ready_to_ship', 'partially_shipped']);

const STATUS_CHIP_COLOR = {
  new: 'default',
  confirmed: 'info',
  in_production: 'warning',
  ready_to_ship: 'secondary',
  partially_shipped: 'warning',
  complete: 'success',
  cancelled: 'error',
};

// Traffic-light row tints: [base background, hover background]
const STATUS_ROW_BG = {
  new: ['#e2e1de', '#d4d3cf'],
  confirmed: ['#c3daf7', '#abcbf3'],
  in_production: ['#ffe396', '#f8d878'],
  ready_to_ship: ['#b5dfe9', '#9dd3e0'],
  partially_shipped: ['#f9d0a8', '#f6c28c'],
  complete: ['#bde4bd', '#a8dba8'],
};

// Local-date yyyy-mm-dd (toISOString would shift a day during BST)
const toDateInput = (d) => {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

const addDaysIso = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return toDateInput(d);
};

const shortDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const fullDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const axisPounds = (v) => (Math.abs(v) >= 1000 ? `£${Math.round(v / 1000)}k` : `£${v}`);

function KpiCard({ label, value, sub, accent, onClick }) {
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        p: 2,
        height: '100%',
        ...(onClick && {
          cursor: 'pointer',
          transition: 'border-color 120ms, box-shadow 120ms',
          '&:hover': { borderColor: 'primary.main', boxShadow: 2 },
        }),
      }}
    >
      <Typography
        variant="overline"
        sx={{ color: 'text.secondary', lineHeight: 1.6, display: 'block' }}
      >
        {label}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 600, color: accent || 'text.primary' }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {sub}
        </Typography>
      )}
    </Paper>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <Paper elevation={3} sx={{ p: 1.5 }}>
      <Typography variant="subtitle2">{label}</Typography>
      {payload.map((entry) => (
        <Box key={entry.dataKey} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: entry.color }} />
          <Typography variant="body2">
            {entry.name}: {gbp0.format(entry.value)}
            {entry.payload[`${entry.dataKey}_orders`] != null &&
              ` · ${entry.payload[`${entry.dataKey}_orders`]} orders`}
          </Typography>
        </Box>
      ))}
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        Click bar to see these orders
      </Typography>
    </Paper>
  );
}

// recharts passes either the entry itself or a props object with .payload
const barPeriod = (d) => (d && (d.period || (d.payload && d.payload.period))) || null;

function AdherenceChart({ rows, series, weekly, onBarClick, todayPeriod }) {
  const todayRow = todayPeriod ? rows.find((r) => r.period === todayPeriod) : null;
  return (
    <ResponsiveContainer width="100%" height={340}>
      <BarChart data={rows} barGap={2} margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
        <CartesianGrid vertical={false} stroke={GRIDLINE} />
        <XAxis
          dataKey="label"
          tick={{ fill: INK_MUTED, fontSize: 12 }}
          tickLine={false}
          axisLine={{ stroke: GRIDLINE }}
          interval="preserveStartEnd"
          minTickGap={24}
        />
        <YAxis
          tickFormatter={axisPounds}
          tick={{ fill: INK_MUTED, fontSize: 12 }}
          tickLine={false}
          axisLine={false}
          width={56}
        />
        <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
        <Legend wrapperStyle={{ fontSize: 13 }} />
        {todayRow && (
          <ReferenceLine
            x={todayRow.label}
            stroke={INK_MUTED}
            strokeDasharray="4 4"
            label={{
              value: weekly ? 'This week' : 'Today',
              fill: INK_MUTED,
              fontSize: 11,
              position: 'insideTopRight',
            }}
          />
        )}
        {series.includes('due') && (
          <Bar
            dataKey="due"
            name={weekly ? 'Output Due (by due date)' : 'Output Due'}
            fill={COLOR_DUE}
            radius={[4, 4, 0, 0]}
            maxBarSize={22}
            cursor={onBarClick ? 'pointer' : undefined}
            onClick={onBarClick ? (d) => onBarClick(barPeriod(d), 'due') : undefined}
          />
        )}
        {series.includes('received') && (
          <Bar
            dataKey="received"
            name={weekly ? 'Orders Received (by order date)' : 'Orders Received'}
            fill={COLOR_RECEIVED}
            radius={[4, 4, 0, 0]}
            maxBarSize={22}
            cursor={onBarClick ? 'pointer' : undefined}
            onClick={onBarClick ? (d) => onBarClick(barPeriod(d), 'received') : undefined}
          />
        )}
      </BarChart>
    </ResponsiveContainer>
  );
}

function ExportDialog({ open, onClose }) {
  const today = new Date();
  const [startDate, setStartDate] = useState(toDateInput(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 90)));
  const [endDate, setEndDate] = useState(toDateInput(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 60)));
  const [granularity, setGranularity] = useState('weekly');
  const [includeSummary, setIncludeSummary] = useState(true);
  const [includeOrders, setIncludeOrders] = useState(true);
  const [dateMode, setDateMode] = useState('due');
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  const handleDownload = async () => {
    setDownloading(true);
    setError('');
    try {
      const resp = await manufacturingAPI.reports.downloadPlannedAdherence({
        start_date: startDate,
        end_date: endDate,
        granularity,
        include_summary: includeSummary,
        include_orders: includeOrders,
        date_mode: dateMode,
        only_open: onlyOpen,
      });
      const blob = new Blob([resp.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Planned_Adherence_${startDate}_${endDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      onClose();
    } catch (e) {
      setError('Export failed — check the dates and try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Export to Excel</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="From"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="To"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
          <FormControl fullWidth>
            <InputLabel>Summary granularity</InputLabel>
            <Select
              value={granularity}
              label="Summary granularity"
              onChange={(e) => setGranularity(e.target.value)}
            >
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="both">Both (two sheets)</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeSummary}
                onChange={(e) => setIncludeSummary(e.target.checked)}
              />
            }
            label="Include summary sheet (per period: due £, received £, net)"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={includeOrders}
                onChange={(e) => setIncludeOrders(e.target.checked)}
              />
            }
            label="Include order detail sheet"
          />
          <FormControl fullWidth disabled={!includeOrders}>
            <InputLabel>Order sheet filtered by</InputLabel>
            <Select
              value={dateMode}
              label="Order sheet filtered by"
              onChange={(e) => setDateMode(e.target.value)}
            >
              <MenuItem value="due">Due date</MenuItem>
              <MenuItem value="received">Order (received) date</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={onlyOpen}
                onChange={(e) => setOnlyOpen(e.target.checked)}
                disabled={!includeOrders}
              />
            }
            label="Only orders still to despatch"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleDownload}
          disabled={downloading || (!includeSummary && !includeOrders)}
          startIcon={downloading ? <CircularProgress size={16} /> : <FileDownloadIcon />}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DataTableTab({ preset, onOpenOrder }) {
  const todayIso = toDateInput(new Date());
  const [start, setStart] = useState(todayIso);
  const [end, setEnd] = useState(addDaysIso(todayIso, 30));
  const [dateMode, setDateMode] = useState('due');
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState(ALL_STATUS_KEYS);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'due_date', dir: 'asc' });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Drill-through from KPI cards / chart bars
  useEffect(() => {
    if (!preset) return;
    setStart(preset.start);
    setEnd(preset.end);
    setDateMode(preset.dateMode || 'due');
    setOnlyOpen(Boolean(preset.onlyOpen));
    setStatusFilter(ALL_STATUS_KEYS);
    setSearch('');
  }, [preset]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await manufacturingAPI.reports.plannedAdherenceOrders({
        start,
        end,
        date_mode: dateMode,
        only_open: onlyOpen ? '1' : '0',
      });
      setRows(resp.data.orders);
    } catch (e) {
      setError('Could not load orders for that range.');
    } finally {
      setLoading(false);
    }
  }, [start, end, dateMode, onlyOpen]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const displayed = useMemo(() => {
    const text = search.trim().toLowerCase();
    const filtered = rows.filter((r) =>
      statusFilter.includes(r.status) &&
      (!text ||
        r.order_number.toLowerCase().includes(text) ||
        r.customer_name.toLowerCase().includes(text))
    );
    const dir = sort.dir === 'asc' ? 1 : -1;
    const sortValue = (r) => {
      if (sort.key === 'value') return parseFloat(r.value) || 0;
      if (sort.key === 'status') return r.status_label || '';
      return r[sort.key] || '';
    };
    return [...filtered].sort((a, b) => {
      const av = sortValue(a);
      const bv = sortValue(b);
      if (av < bv) return -dir;
      if (av > bv) return dir;
      return 0;
    });
  }, [rows, statusFilter, search, sort]);

  const totalDisplayed = useMemo(
    () => displayed.reduce((sum, r) => sum + (parseFloat(r.value) || 0), 0),
    [displayed]
  );

  const toggleSort = (key) =>
    setSort((prev) => ({
      key,
      dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc',
    }));

  const headCell = (key, label, align) => (
    <TableCell align={align} sortDirection={sort.key === key ? sort.dir : false}>
      <TableSortLabel
        active={sort.key === key}
        direction={sort.key === key ? sort.dir : 'asc'}
        onClick={() => toggleSort(key)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const applyRange = (s, e, opts = {}) => {
    setStart(s);
    setEnd(e);
    setDateMode(opts.dateMode || 'due');
    setOnlyOpen(Boolean(opts.onlyOpen));
  };

  const mondayIso = (() => {
    const d = new Date();
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return toDateInput(d);
  })();

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
        <Chip size="small" label="This Week" onClick={() => applyRange(mondayIso, addDaysIso(mondayIso, 6))} />
        <Chip size="small" label="Next 30 Days" onClick={() => applyRange(todayIso, addDaysIso(todayIso, 30))} />
        <Chip size="small" label="Last 30 Days" onClick={() => applyRange(addDaysIso(todayIso, -30), todayIso)} />
        <Chip
          size="small"
          label="Overdue (open)"
          onClick={() => applyRange(addDaysIso(todayIso, -730), addDaysIso(todayIso, -1), { onlyOpen: true })}
          sx={{ color: OVERDUE_RED }}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <TextField
          label="From"
          type="date"
          size="small"
          value={start}
          onChange={(e) => setStart(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          value={end}
          onChange={(e) => setEnd(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Date filter</InputLabel>
          <Select
            value={dateMode}
            label="Date filter"
            onChange={(e) => setDateMode(e.target.value)}
          >
            <MenuItem value="due">Due date</MenuItem>
            <MenuItem value="received">Order (received) date</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ width: 190 }}>
          <InputLabel>Statuses</InputLabel>
          <Select
            multiple
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            input={<OutlinedInput label="Statuses" />}
            // Fixed-width label + a non-repositioning menu: without these the
            // rendered text resizes the control and MUI's default
            // 'selectedMenu' variant re-anchors the popover on every click,
            // so the whole toolbar and dropdown jump around while selecting.
            renderValue={(selected) => {
              if (selected.length === ALL_STATUS_KEYS.length) return 'All statuses';
              if (selected.length === 0) return 'None';
              if (selected.length === 1) return STATUS_LABELS[selected[0]];
              return `${selected.length} selected`;
            }}
            MenuProps={{
              variant: 'menu',
              PaperProps: { style: { maxHeight: 320 } },
            }}
          >
            {ALL_STATUS_KEYS.map((key) => (
              <MenuItem key={key} value={key} dense>
                <Checkbox checked={statusFilter.includes(key)} size="small" />
                <ListItemText primary={STATUS_LABELS[key]} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          placeholder="Search order # / customer"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 220 }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={onlyOpen}
              onChange={(e) => setOnlyOpen(e.target.checked)}
              size="small"
            />
          }
          label="Still to despatch only"
        />
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {displayed.length} orders · {gbp0.format(totalDisplayed)} ex VAT
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 560 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {headCell('order_number', 'Order #')}
              {headCell('customer_name', 'Customer')}
              {headCell('order_date', 'Order Date')}
              {headCell('due_date', 'Due Date')}
              {headCell('status', 'Status')}
              {headCell('value', 'Value (ex VAT)', 'right')}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : displayed.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No orders match
                </TableCell>
              </TableRow>
            ) : (
              displayed.map((row) => {
                const [bg, bgHover] = STATUS_ROW_BG[row.status] || ['transparent', undefined];
                const overdue =
                  OPEN_STATUSES.has(row.status) && row.due_date && row.due_date < todayIso;
                return (
                  <TableRow
                    key={row.id}
                    onClick={() => onOpenOrder(row.id)}
                    sx={{
                      bgcolor: bg,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: bgHover || 'action.hover' },
                      '& td:first-of-type': {
                        borderLeft: `4px solid ${overdue ? OVERDUE_RED : 'transparent'}`,
                      },
                    }}
                  >
                    <TableCell>{row.order_number}</TableCell>
                    <TableCell>{row.customer_name}</TableCell>
                    <TableCell>{fullDate(row.order_date)}</TableCell>
                    <TableCell sx={overdue ? { color: '#b71c1c', fontWeight: 600 } : undefined}>
                      {fullDate(row.due_date)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.status_label}
                        size="small"
                        color={STATUS_CHIP_COLOR[row.status] || 'default'}
                        variant="outlined"
                        sx={{ bgcolor: 'rgba(255,255,255,0.6)' }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {gbp2.format(parseFloat(row.value) || 0)}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
        Click a row to open the order · row colour = status · red edge = open order past its due date
      </Typography>
    </Box>
  );
}

export default function PlannedAdherence() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [series, setSeries] = useState(['due', 'received']);
  const [dailyFilter, setDailyFilter] = useState('next30');
  const [weeklyFilter, setWeeklyFilter] = useState('recent');
  const [exportOpen, setExportOpen] = useState(false);
  const [tablePreset, setTablePreset] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await manufacturingAPI.reports.plannedAdherence();
        if (!cancelled) setData(resp.data);
      } catch (e) {
        if (!cancelled) setError('Could not load the dashboard data.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const openOrder = useCallback(
    (id) => navigate(`/manufacturing/customer-orders/${id}`),
    [navigate]
  );

  const openTableWith = useCallback((params) => {
    setTablePreset({ ...params, nonce: Date.now() });
    setTab(2);
  }, []);

  const onWeeklyBar = useCallback(
    (period, mode) => {
      if (period) openTableWith({ start: period, end: addDaysIso(period, 6), dateMode: mode });
    },
    [openTableWith]
  );

  const onDailyBar = useCallback(
    (period, mode) => {
      if (period) openTableWith({ start: period, end: period, dateMode: mode });
    },
    [openTableWith]
  );

  const chartRows = useCallback((buckets, labelFn) =>
    buckets.map((b) => ({
      label: labelFn(b.period),
      period: b.period,
      due: parseFloat(b.due_value),
      due_orders: b.due_orders,
      received: parseFloat(b.received_value),
      received_orders: b.received_orders,
    })), []);

  const weeklyRows = useMemo(() => {
    if (!data) return [];
    let buckets = data.weekly;
    if (weeklyFilter === 'recent') {
      const from = new Date();
      from.setDate(from.getDate() - 8 * 7);
      const to = new Date();
      to.setDate(to.getDate() + 8 * 7);
      buckets = buckets.filter(
        (b) => b.period >= toDateInput(from) && b.period <= toDateInput(to)
      );
    }
    return chartRows(buckets, (p) => `w/c ${shortDate(p)}`);
  }, [data, weeklyFilter, chartRows]);

  const dailyRows = useMemo(() => {
    if (!data) return [];
    const today = data.as_of;
    let buckets = data.daily;
    if (dailyFilter === 'next30') {
      const to = new Date();
      to.setDate(to.getDate() + 30);
      buckets = buckets.filter((b) => b.period >= today && b.period <= toDateInput(to));
    } else if (dailyFilter === 'historical') {
      buckets = buckets.filter((b) => b.period < today);
    }
    return chartRows(buckets, shortDate);
  }, [data, dailyFilter, chartRows]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error || 'No data available.'}</Alert>
      </Box>
    );
  }

  const k = data.kpis;
  const money = (kpi) => gbp0.format(parseFloat(kpi.value) || 0);
  const asOf = data.as_of;
  const weekStart = data.week_start;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 1 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Planned Adherence
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Customer orders · output due by due date vs orders received · values ex VAT ·
            cancelled orders excluded · click a card or bar to see its orders
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={() => setExportOpen(true)}
        >
          Export
        </Button>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Weekly View" />
        <Tab label="Daily View" />
        <Tab label="Data Table" />
      </Tabs>

      {tab === 0 && (
        <Box>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <KpiCard
                label="This Week Output Due"
                value={money(k.this_week_due)}
                sub={`${k.this_week_due.orders} orders · w/c ${shortDate(weekStart)}`}
                onClick={() => openTableWith({ start: weekStart, end: addDaysIso(weekStart, 6) })}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <KpiCard
                label="Next Week Output Due"
                value={money(k.next_week_due)}
                sub={`${k.next_week_due.orders} orders`}
                onClick={() => openTableWith({ start: addDaysIso(weekStart, 7), end: addDaysIso(weekStart, 13) })}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <KpiCard
                label="This Week Sales In"
                value={money(k.this_week_received)}
                sub={`${k.this_week_received.orders} orders received`}
                onClick={() => openTableWith({ start: weekStart, end: addDaysIso(weekStart, 6), dateMode: 'received' })}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <KpiCard
                label="Last Week Sales In"
                value={money(k.last_week_received)}
                sub={`${k.last_week_received.orders} orders received`}
                onClick={() => openTableWith({ start: addDaysIso(weekStart, -7), end: addDaysIso(weekStart, -1), dateMode: 'received' })}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <KpiCard
                label="Forecast Pipeline"
                value={money(k.pipeline)}
                sub={`${k.pipeline.orders} orders still to despatch`}
                onClick={() => openTableWith({ start: addDaysIso(asOf, -730), end: addDaysIso(asOf, 365), onlyOpen: true })}
              />
            </Grid>
            {k.overdue.orders > 0 && (
              <Grid item xs={12} sm={6} md={2.4}>
                <KpiCard
                  label="Overdue Backlog"
                  value={money(k.overdue)}
                  sub={`${k.overdue.orders} open orders past due`}
                  accent="error.main"
                  onClick={() => openTableWith({ start: addDaysIso(asOf, -730), end: addDaysIso(asOf, -1), onlyOpen: true })}
                />
              </Grid>
            )}
          </Grid>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
                Weekly Output Due vs Orders Received (£ ex VAT)
              </Typography>
              <ToggleButtonGroup
                size="small"
                value={series}
                onChange={(_, v) => v.length && setSeries(v)}
              >
                <ToggleButton value="due">Output Due</ToggleButton>
                <ToggleButton value="received">Orders Received</ToggleButton>
              </ToggleButtonGroup>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={weeklyFilter}
                onChange={(_, v) => v && setWeeklyFilter(v)}
              >
                <ToggleButton value="recent">±8 Weeks</ToggleButton>
                <ToggleButton value="all">All</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <AdherenceChart
              rows={weeklyRows}
              series={series}
              weekly
              onBarClick={onWeeklyBar}
              todayPeriod={weekStart}
            />
          </Paper>
        </Box>
      )}

      {tab === 1 && (
        <Box>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <KpiCard
                label={`Today Due (${shortDate(asOf)})`}
                value={money(k.today_due)}
                sub={`${k.today_due.orders} orders`}
                onClick={() => openTableWith({ start: asOf, end: asOf })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <KpiCard
                label="Tomorrow Due"
                value={money(k.tomorrow_due)}
                sub={`${k.tomorrow_due.orders} orders`}
                onClick={() => openTableWith({ start: addDaysIso(asOf, 1), end: addDaysIso(asOf, 1) })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <KpiCard
                label="This Week Total"
                value={money(k.this_week_due)}
                sub={`${k.this_week_due.orders} orders · w/c ${shortDate(weekStart)}`}
                onClick={() => openTableWith({ start: weekStart, end: addDaysIso(weekStart, 6) })}
              />
            </Grid>
          </Grid>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
                Daily Output Due (£ ex VAT)
              </Typography>
              <ToggleButtonGroup
                size="small"
                value={series}
                onChange={(_, v) => v.length && setSeries(v)}
              >
                <ToggleButton value="due">Output Due</ToggleButton>
                <ToggleButton value="received">Orders Received</ToggleButton>
              </ToggleButtonGroup>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={dailyFilter}
                onChange={(_, v) => v && setDailyFilter(v)}
              >
                <ToggleButton value="next30">Next 30 Days</ToggleButton>
                <ToggleButton value="historical">Historical</ToggleButton>
                <ToggleButton value="all">All</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <AdherenceChart
              rows={dailyRows}
              series={series}
              onBarClick={onDailyBar}
              todayPeriod={asOf}
            />
          </Paper>
        </Box>
      )}

      {tab === 2 && <DataTableTab preset={tablePreset} onOpenOrder={openOrder} />}

      <ExportDialog open={exportOpen} onClose={() => setExportOpen(false)} />
    </Box>
  );
}
